import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import axios from "axios";
import { Navigation } from "../sections/Navigation";
import { Footer } from "../sections/Footer";
import { WhatsAppButton } from "../sections/WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = { plates: "Plates of the Month", sips: "Sips of the Month", bakes: "Bakes of the Month" };

const menuCategories = [
  { id: "beverages", name: "Beverages", image: "/menu-images/beverages.jpg" },
  { id: "appetizers", name: "Appetizers", image: "/menu-images/appetizers.jpg" },
  { id: "breakfast", name: "Breakfast", image: "/menu-images/breakfast.jpg" },
  { id: "salads", name: "Salads & Soups", image: "/menu-images/salads.jpg" },
  { id: "sandwiches", name: "Sandwiches", image: "/menu-images/sandwiches.jpg" },
  { id: "pasta", name: "Pasta", image: "/menu-images/pasta.jpg" },
  { id: "pizza", name: "Pizza", image: "/menu-images/pizza.jpg" },
  { id: "rice_bowls", name: "Rice Bowls", image: "/menu-images/rice_bowls.jpg" },
  { id: "mains", name: "Mains", image: "/menu-images/mains.jpg" },
  { id: "casa_loco", name: "Casa Loco", image: "/menu-images/casa_loco.jpg" },
  { id: "desserts", name: "Desserts", image: "/menu-images/desserts.jpg" },
  { id: "viennoiserie", name: "Viennoiserie", image: "/menu-images/viennoiserie.jpg" },
  { id: "cookies_bread", name: "Bakery", image: "/menu-images/cookies_bread.jpg" },
];

export default function MenuPage() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("beverages");
  const [specials, setSpecials] = useState([]);
  const [specialsMonth, setSpecialsMonth] = useState("September");

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const [menuRes, specialsRes] = await Promise.all([
          axios.get(`${API}/menu`),
          axios.get(`${API}/specials`),
        ]);
        setMenuItems(menuRes.data);
        if (specialsRes.data.length > 0) {
          setSpecials(specialsRes.data);
          setSpecialsMonth(specialsRes.data[0].month || "August");
        }
      } catch (e) {
        console.error("Failed to load data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredItems = menuItems.filter((item) => item.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#EEE8DC]">
      <Navigation />

      {/* Hero banner */}
      <section className="pt-24 pb-12 md:pt-28 md:pb-16 bg-[#191646]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#C9A85A] font-body text-sm tracking-wider uppercase mb-8 transition-colors" data-testid="menu-back-home">
            <ArrowLeft size={16} /> Home
          </Link>
          <p className="text-[#C9A85A] text-sm tracking-[0.2em] uppercase font-body font-semibold mb-3">Our Menu</p>
          <h1 className="font-heading text-5xl md:text-6xl text-white font-medium tracking-tight mb-4">From Our Kitchen</h1>
          <p className="text-white/50 font-body max-w-lg">Freshly baked. European at heart. Every dish crafted with passion.</p>
        </div>
      </section>

      {/* Sticky category bar */}
      <div className="sticky top-20 z-30 bg-[#EEE8DC] border-b border-[#DDD6C8] py-3">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {menuCategories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`menu-category-${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex-shrink-0 px-4 py-2 text-xs font-body font-semibold tracking-[0.1em] uppercase border transition-all duration-200 ${
                  activeCategory === cat.id
                    ? "bg-[#191646] text-[#C9A85A] border-[#191646]"
                    : "bg-transparent text-[#4A5568] border-[#DDD6C8] hover:border-[#C9A85A] hover:text-[#191646]"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu items */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-3xl text-[#191646] font-medium">
              {menuCategories.find((c) => c.id === activeCategory)?.name}
            </h2>
            <span className="text-[#4A5568] text-sm font-body">{filteredItems.length} items</span>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-[#E3DACB] mb-3" />
                  <div className="h-4 bg-[#E3DACB] rounded mb-2 w-3/4" />
                  <div className="h-3 bg-[#E3DACB] rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {filteredItems.map((item, index) => (
                <div key={item.id || index} className="group" data-testid={`menu-item-${index}`}>
                  <div className="relative aspect-[4/3] overflow-hidden mb-3 hover-zoom">
                    <img loading="lazy" src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    {item.is_featured && (
                      <div className="absolute top-2 left-2 bg-[#C9A85A] text-[#191646] text-[10px] font-body font-semibold px-2 py-0.5 tracking-wider uppercase">
                        Chef's Pick
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <h3 className="font-heading text-base text-[#191646] font-medium group-hover:text-[#C9A85A] transition-colors">
                        {item.name}
                      </h3>
                      <p className="text-[#4A5568] text-xs font-body mt-1 line-clamp-2">{item.description}</p>
                    </div>
                    {item.price > 0 && (
                      <span className="text-[#C9A85A] font-heading text-base font-semibold whitespace-nowrap">&#8377;{item.price}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white/50 border border-[#DDD6C8]">
              <p className="text-[#4A5568] font-body">Items for this category coming soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Monthly Specials */}
      {specials.length > 0 && (
        <section className="py-16 md:py-20 bg-[#191646]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles className="text-[#C9A85A]" size={20} />
              <h2 className="font-heading text-3xl text-white font-medium">{specialsMonth} Specials</h2>
              <div className="ml-3 bg-[#C9A85A]/15 border border-[#C9A85A]/30 px-3 py-1">
                <span className="text-[#C9A85A] text-[10px] font-body font-semibold tracking-[0.2em] uppercase">Limited Time</span>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {["plates", "sips", "bakes"].map((cat) => {
                const items = specials.filter((s) => s.category === cat);
                if (items.length === 0) return null;
                return (
                  <div key={cat} className="bg-white/5 border border-white/10 p-6">
                    <h3 className="text-[#C9A85A] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-5">
                      {CATEGORY_LABELS[cat] || cat}
                    </h3>
                    <div className="space-y-0">
                      {items.map((item) => (
                        <div key={item.id} className="flex gap-4 px-5 py-3 hover:bg-white/5 transition-colors">
                          {item.image_url && (
                            <img loading="lazy" src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded flex-shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                              <h4 className="font-heading text-sm text-white font-medium mb-0.5 leading-tight">{item.name}</h4>
                              {item.price > 0 && <span className="text-[#C9A85A] text-sm font-heading font-medium flex-shrink-0">&#8377;{item.price}</span>}
                            </div>
                            <p className="text-white/40 text-[11px] font-body leading-relaxed line-clamp-2">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
